import { z } from "zod";
import { TraineeDetailsFormValues } from "./TraineeDetailsFormModal.types";

export type NamedChannelRow = {
  channelName?: string;
  link?: string;
  language?: string;
};

const getUrlParts = (
  url: string
): { host: string; pathname: string } | null => {
  try {
    const parsed = new URL(url);
    if (!/^https?:$/i.test(parsed.protocol)) {
      return null;
    }
    return {
      host: parsed.hostname.toLowerCase(),
      pathname: parsed.pathname.replace(/\/+$/, "") || "/",
    };
  } catch {
    return null;
  }
};

const isYouTubeHost = (host: string): boolean =>
  host === "youtube.com" || host.endsWith(".youtube.com");

const isInstagramHost = (host: string): boolean =>
  host === "instagram.com" || host.endsWith(".instagram.com");

/** Channel path: /@handle, /channel/ID, /c/name, or /user/name */
const isYouTubeChannelPath = (pathname: string): boolean =>
  /^\/@[A-Za-z0-9._-]+$/i.test(pathname) ||
  /^\/channel\/[A-Za-z0-9_-]+$/i.test(pathname) ||
  /^\/c\/[A-Za-z0-9._-]+$/i.test(pathname) ||
  /^\/user\/[A-Za-z0-9._-]+$/i.test(pathname);

const INSTAGRAM_RESERVED_PATHS = new Set([
  "p",
  "reel",
  "reels",
  "stories",
  "explore",
  "accounts",
  "about",
  "legal",
  "direct",
  "tv",
  "graphql",
  "api",
]);

/** Profile path: /username (not homepage, post, reel, etc.) */
const isInstagramProfilePath = (pathname: string): boolean => {
  const match = pathname.match(/^\/([A-Za-z0-9._]+)$/);
  if (!match) {
    return false;
  }
  const username = match[1].toLowerCase();
  return (
    !INSTAGRAM_RESERVED_PATHS.has(username) &&
    username.length >= 1 &&
    username.length <= 30
  );
};

export const isYouTubeUrl = (url: string): boolean => {
  const parts = getUrlParts(url);
  if (!parts) {
    return false;
  }
  return isYouTubeHost(parts.host) && isYouTubeChannelPath(parts.pathname);
};

export const isInstagramUrl = (url: string): boolean => {
  const parts = getUrlParts(url);
  if (!parts) {
    return false;
  }
  return isInstagramHost(parts.host) && isInstagramProfilePath(parts.pathname);
};

export const isNamedChannelRowStarted = (ch: NamedChannelRow): boolean =>
  !!(
    ch.channelName?.trim() ||
    ch.link?.trim() ||
    ch.language?.trim()
  );

export const isNamedChannelRowComplete = (
  ch: NamedChannelRow,
  isValidLink: (url: string) => boolean = (url) => /^https?:\/\//i.test(url)
): boolean =>
  !!(
    ch.channelName?.trim() &&
    ch.link?.trim() &&
    ch.language?.trim() &&
    isValidLink(ch.link.trim())
  );

type NamedChannelLinkOptions = {
  isValidLink: (url: string) => boolean;
  invalidLinkMessage: string;
};

const validateNamedChannelFields = (
  ch: NamedChannelRow,
  ctx: z.RefinementCtx,
  options: NamedChannelLinkOptions
): void => {
  if (!isNamedChannelRowStarted(ch)) {
    return;
  }

  if (!ch.channelName?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Channel name is required",
      path: ["channelName"],
    });
  }

  if (!ch.link?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Channel link is required",
      path: ["link"],
    });
  } else if (!options.isValidLink(ch.link.trim())) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: options.invalidLinkMessage,
      path: ["link"],
    });
  }

  if (!ch.language?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: "Channel language is required",
      path: ["language"],
    });
  }
};

const youtubeChannelSchema = z
  .object({
    channelName: z.string().optional().default(""),
    link: z
      .string()
      .optional()
      .default("")
      .refine(
        (val) => !val || isYouTubeUrl(val),
        "Enter a valid YouTube channel URL (e.g. https://youtube.com/@yourchannel)"
      ),
    language: z.string().optional().default(""),
  })
  .superRefine((ch, ctx) =>
    validateNamedChannelFields(ch, ctx, {
      isValidLink: isYouTubeUrl,
      invalidLinkMessage:
        "Enter a valid YouTube channel URL (e.g. https://youtube.com/@yourchannel)",
    })
  );

const instagramChannelSchema = z
  .object({
    channelName: z.string().optional().default(""),
    link: z
      .string()
      .optional()
      .default("")
      .refine(
        (val) => !val || isInstagramUrl(val),
        "Enter a valid Instagram profile URL (e.g. https://instagram.com/yourusername)"
      ),
    language: z.string().optional().default(""),
  })
  .superRefine((ch, ctx) =>
    validateNamedChannelFields(ch, ctx, {
      isValidLink: isInstagramUrl,
      invalidLinkMessage:
        "Enter a valid Instagram profile URL (e.g. https://instagram.com/yourusername)",
    })
  );

const otherChannelSchema = z.object({
  name: z.string().optional().default(""),
  link: z
    .string()
    .optional()
    .default("")
    .refine(
      (val) => !val || /^https?:\/\//i.test(val),
      "Enter a valid URL starting with http or https"
    ),
  platform: z.string().optional().default(""),
});

export const traineeDetailsSchema = z
  .object({
    name: z.string().min(2, "Full name must be at least 2 characters").max(100),
    email: z.string().email("Please enter a valid email address"),
    whatsappNumber: z.string().min(8, "Enter a valid WhatsApp number"),
    dateOfBirth: z.string().min(1, "Date of birth is required"),
    profession: z.string().min(1, "Profession is required"),
    experienceYears: z.string().min(1, "Experience is required"),
    education: z.string().min(1, "Education is required"),
    location: z.string().min(1, "Location is required"),
    ishaPrograms: z.string().min(1, "Please mention Isha programs"),
    youtubeChannels: z.array(youtubeChannelSchema).default([]),
    instagramChannels: z.array(instagramChannelSchema).default([]),
    audiencePersona: z.string().min(1, "Audience persona is required"),
    channelGenre: z.string().min(1, "Channel genre is required"),
    videoFormat: z.string().min(1, "Video format is required"),
    strengths: z.string().min(1, "Strengths are required"),
    contentCreationChallenges: z
      .string()
      .min(1, "Content creation challenges are required"),
    postingRegularly: z.preprocess(
      (val) => (val === null ? undefined : val),
      z.enum(["Yes", "No", "Sometimes"], {
        required_error: "Please select an option",
        invalid_type_error: "Please select an option",
      })
    ),
    postingChallenge: z.string(),
    channelGrowthJourney: z
      .string()
      .min(1, "Channel growth journey is required")
      .max(1000, "Maximum 1000 characters"),
    otherChannels: z.array(otherChannelSchema).default([]),
    channelsMonetized: z.preprocess(
      (val) => (val === null ? undefined : val),
      z.enum(["Yes", "No"], {
        required_error: "Please select an option",
        invalid_type_error: "Please select an option",
      })
    ),
    monetizedChannelsDetails: z
      .string()
      .max(1000, "Maximum 1000 characters")
      .optional()
      .default(""),
    teamNotes: z
      .string()
      .min(1, "Please share something with the team")
      .max(1000, "Maximum 1000 characters"),
  })
  .superRefine((value, ctx) => {
    if (value.postingRegularly === "No") {
      if (!value.postingChallenge || !value.postingChallenge.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            'Please describe the main bottleneck when you select "No" for posting regularly.',
          path: ["postingChallenge"],
        });
      }
    }

    if (value.channelsMonetized === "Yes") {
      if (
        !value.monetizedChannelsDetails ||
        !value.monetizedChannelsDetails.trim()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Please provide monetization details",
          path: ["monetizedChannelsDetails"],
        });
      }
    }

    const hasYouTube = (value.youtubeChannels ?? []).some((ch) =>
      isNamedChannelRowComplete(ch, isYouTubeUrl)
    );
    const hasInstagram = (value.instagramChannels ?? []).some((ch) =>
      isNamedChannelRowComplete(ch, isInstagramUrl)
    );
    const hasOther = (value.otherChannels ?? []).some(
      (ch) =>
        (ch.name && ch.name.trim().length > 0) ||
        (ch.link && ch.link.trim().length > 0)
    );

    if (!hasYouTube && !hasInstagram && !hasOther) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "At least one channel is required: fill in YouTube, Instagram, or an Additional channel.",
        path: ["youtubeChannels", "root"],
      });
    }
  });

export type TraineeDetailsFormSchema = z.infer<typeof traineeDetailsSchema>;

export function getTraineeDetailsValidationError(
  values: TraineeDetailsFormValues
): string | undefined {
  const result = traineeDetailsSchema.safeParse(values);
  if (result.success) {
    return undefined;
  }

  return result.error.issues[0]?.message ?? "Please fix the highlighted fields.";
}

export function assertTraineeDetailsFormValues(
  values: TraineeDetailsFormValues
): void {
  const message = getTraineeDetailsValidationError(values);
  if (message) {
    throw new Error(message);
  }
}
